// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  Icon,
  // Tooltip,
  CircularProgress,
  Backdrop,
  ListItem,
  ListItemText,
  Typography,
  ListItemIcon,
  Button,
  Divider,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import AppConfig from "appConfig";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
// import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

function CommentCampaignsPage() {
  const pageTitle = `Comment Campaigns`;
  usePageTitle(pageTitle);
  const navigate = useNavigate();

  function emptyCache() {
    if ("caches" in window) {
      caches.keys().then((names) => {
        // Delete all the cache files
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
  }

  function versionRefresh() {
    emptyCache();
    window.location.reload();
  }

  function appVersionCheck(newVersion) {
    // Handle App Update
    const currentVersion = localStorage.getItem(AppConfig.app_version_store);
    let shouldUpdate = false;
    if (currentVersion !== newVersion) {
      shouldUpdate = true;
    }
    localStorage.setItem(AppConfig.app_version_store, newVersion);

    if (shouldUpdate) {
      if (
        window.confirm(
          "A new version is available. App will automatically refresh to get the latest update."
        ) === true
      ) {
        versionRefresh();
      } else {
        versionRefresh();
      }
    }
  }

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [pageLoaded, setPageLoaded] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`agents/smm/comment/campaigns.json`)
        .then((result) => {
          setShowLoading(false);
          // console.log(result);
          if (result.data.status === "success") {
            setCampaigns(result.data.data.campaigns);
          } else {
            setErrMsg(result.data.data.message);
            setSeverity("error");
            setOpen(true);
          }
          setPageLoaded(true);
          appVersionCheck(result.data.version);
        })
        .catch(() => {
          setShowLoading(false);
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setSeverity("error");
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox mb={5}>
        {pageLoaded && (
          <MDBox mt={3}>
            <MDBox>
              <Button
                size="small"
                color="dark"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  navigate(`/social`);
                }}
              >
                Back
              </Button>
            </MDBox>
            <MDBox mt={2}>
              <Card sx={{ width: "100%", height: "100%" }}>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                    Comment Campaigns
                  </MDTypography>
                  <MDTypography variant="subtitle2" fontSize={15} fontWeight="light" gutterBottom>
                    Get paid to comment and reply posts from verified brands on social media.
                  </MDTypography>
                </MDBox>
              </Card>
            </MDBox>
            <MDBox mt={5}>
              <Divider />
            </MDBox>
            <MDBox mt={1}>
              {campaigns.length <= 0 ? (
                <Card sx={{ width: "100%", marginTop: "35px" }}>
                  <MDBox pt={3} pb={2} px={2}>
                    <MDBox
                      component="ul"
                      display="flex"
                      flexDirection="column"
                      p={0}
                      m={0}
                      sx={{ listStyle: "none" }}
                    >
                      <MDTypography variant="h6" fontSize={36} align="center">
                        <Icon>campaign</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="light" align="center">
                        No available campaigns at the moment. Check back later.
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              ) : (
                <MDBox
                  mt={5}
                  component="ul"
                  display="flex"
                  flexDirection="column"
                  p={0}
                  sx={{ listStyle: "none" }}
                >
                  <MDBox mb={2} px={2} display="flex" justifyContent="space-between">
                    <MDTypography variant="h6" fontWeight="regular">
                      <b>Campaigns:</b>
                    </MDTypography>
                    <MDTypography variant="h6" fontWeight="regular">
                      {campaigns.length}
                    </MDTypography>
                  </MDBox>
                  {campaigns.map((record) => (
                    <MDBox
                      mb={2}
                      onClick={() => {
                        navigate(`/comment-campaigns/${record.reference}`);
                      }}
                    >
                      <Card sx={{ width: "100%" }}>
                        <MDBox p={2}>
                          <ListItem key={record.reference} sx={{ py: 1, px: 0 }}>
                            <MDBox align="right" sx={{ height: "100%" }}>
                              <ListItemIcon sx={{ height: "100%" }}>
                                <MDBox
                                  component="img"
                                  mr={1}
                                  sx={{
                                    width: { xs: 24, md: 36, lg: 36 },
                                  }}
                                  src={`${AppConfig.api_media_url}${record.platform.logo}`}
                                  alt={record.title}
                                />
                              </ListItemIcon>
                            </MDBox>
                            <ListItemText
                              sx={{ ml: 0 }}
                              primary={
                                <MDBox>
                                  <MDTypography
                                    variant="h6"
                                    fontWeight="regular"
                                    textTransform="capitalize"
                                    fontSize={13}
                                  >
                                    {record.handle}
                                  </MDTypography>
                                </MDBox>
                              }
                              secondary={
                                <MDBox mt={0}>
                                  <MDBox mb={0.5}>
                                    <Typography variant="subtitle2" fontSize={11} fontWeight={400}>
                                      Commission:{" "}
                                      <b>
                                        {record.currency}
                                        {(record.commission / 100).toLocaleString("en", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </b>
                                    </Typography>
                                    <Typography fontSize={10}>
                                      Completed:{" "}
                                      <b>
                                        {record.count} / {record.target}
                                      </b>
                                    </Typography>
                                  </MDBox>
                                </MDBox>
                              }
                            />
                            <MDBox>
                              <MDBox mt={0} align="right">
                                <MDButton variant="outlined" iconOnly circular color="primary">
                                  <Icon>chevron_right</Icon>
                                </MDButton>
                              </MDBox>
                            </MDBox>
                          </ListItem>
                        </MDBox>
                      </Card>
                    </MDBox>
                  ))}
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CommentCampaignsPage;
